import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../state/auth';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, Loader2, Home, User, Clock, Wifi, Briefcase, Thermometer, Layout, Utensils, Droplets, Car,
  BookOpen, ShieldAlert, CheckCircle2, FileText, Key, Contact, MapPin, Heart, MessageSquarePlus
} from "lucide-react";
import { FormContext } from "./kb2/FormComponents";
import { Step1 } from './kb2/Step1';
import { Step2 } from './kb2/Step2';
import { Step3 } from './kb2/Step3';
import { Step4 } from './kb2/Step4';
import { Step5 } from './kb2/Step5';
import { Step6 } from './kb2/Step6';
import { Step7 } from './kb2/Step7';
import { Step8 } from './kb2/Step8';
import { Step9 } from './kb2/Step9';
import { Step10 } from './kb2/Step10';
import { Step11 } from './kb2/Step11';
import { Step12 } from './kb2/Step12';
import { Step13 } from './kb2/Step13';
import { Step14 } from './kb2/Step14';
import { Step15 } from './kb2/Step15';
import { Step16 } from './kb2/Step16';
import { Step17CustomKnowledge } from './kb2/Step17CustomKnowledge';
import { Step17 } from './kb2/Step17';

// --- Steps Metadata ---
const STEPS = [
  { id: 1, name: "Property Profile", icon: Home, key: "propertyProfile" },
  { id: 2, name: "Building Access", icon: Key, key: "buildingAccess" },
  { id: 3, name: "Contact Details", icon: Contact, key: "contactDetails" },
  { id: 4, name: "Check-in", icon: MapPin, key: "checkIn" },
  { id: 5, name: "Check-out", icon: Clock, key: "checkOut" },
  { id: 6, name: "Luggage Storage", icon: Briefcase, key: "luggageStorage" },
  { id: 7, name: "WiFi & Tech", icon: Wifi, key: "wifiTech" },
  { id: 8, name: "Heating, Cooling & Utilities", icon: Thermometer, key: "climateUtilities" },
  { id: 9, name: "Living Spaces", icon: Layout, key: "livingSpaces" },
  { id: 10, name: "Kitchen & Dining", icon: Utensils, key: "kitchenDining" },
  { id: 11, name: "Bathroom & Laundry", icon: Droplets, key: "bathroomLaundry" },
  { id: 12, name: "Parking & Transport", icon: Car, key: "parkingTransport" },
  { id: 13, name: "House Rules", icon: ShieldAlert, key: "houseRules" },
  { id: 14, name: "Local Guide", icon: BookOpen, key: "localGuide" },
  { id: 15, name: "Safety & Emergency", icon: Heart, key: "safetyMaintenance" },
  { id: 16, name: "Policies", icon: FileText, key: "policies" },
  { id: 17, name: "Custom Knowledge", icon: MessageSquarePlus, key: "customKnowledge" },
  { id: 18, name: "Review & Publish", icon: CheckCircle2, key: "review" },
];

// --- Initial Data Structure ---
const initialData = {
  propertyProfile: { name: "", type: "", address: "", floor: "", unitNumber: "", size: "", sizeUnit: "mÂ²" },
  buildingAccess: { buildingRecognition: "", buildingAccessType: "", hasMainEntrance: false, wheelchairAccessible: false, arrivalRoutes: [], intercom: { available: false, instructions: "" }, roomFindingInstructions: "" },
  contactDetails: { mainContactType: "", name: "", phone: "", email: "", availabilityHours: "09:00 - 21:00", messagingApps: { available: false, apps: [], otherApp: "" }, languages: [], otherLanguage: "" },
  checkIn: { methods: [], checkInType: "Specific Time", checkInFrom: "15:00", checkInUntil: "20:00", reception: { location: "", openFrom: "08:00", openUntil: "20:00", checkInRequiredDuringOfficeHours: false }, meetOnArrival: { from: "08:00", until: "20:00", meetingPoint: "", contactPerson: "", instructions: "", afterWindowInstructions: "", isRequiredDuringHours: false }, selfCheckIn: { mainEntranceAccessType: "No, guests can enter the building freely", mainEntranceAccessFrom: "08:00", mainEntranceAccessUntil: "20:00", deviceType: "", location: "", accessCodeTiming: "", accessCodeChannels: [], accessCodeChannelOther: "", instructions: "", lockboxContent: "", hasDifferentDevices: false }, earlyCheckIn: { available: false, feeApplicable: false, feeAmount: "", feeCurrency: "â‚¬" }, guestId: { required: false, requiredForAccessCode: false, channels: [], email: "", phone: "", messagingApp: "", otherMessagingApp: "", timing: "", requiredDocuments: "", acceptAFM: false, acceptTIN: false }, additionalNotes: "" },
  checkOut: { time: "11:00", keyReturn: "", tasks: "", checkoutCleaning: { expected: false, tasks: [], otherTask: "" }, lateCheckOut: { available: false, feeApplicable: false, feeAmount: "", feeCurrency: "â‚¬" }, stayExtension: { available: false }, buildingExit: { accessType: "No, the building exit opens freely from inside", accessFrom: "22:00", accessUntil: "07:00", exitInstructions: "" } },
  luggageStorage: { available: false, beforeCheckIn: { available: false, location: "", from: "08:00", until: "15:00", instructions: "" }, afterCheckOut: { available: false, location: "", from: "11:00", until: "18:00", instructions: "" }, nearbyLockers: { available: false, lockers: [] } },
  wifiTech: { available: true, ssid: "", password: "", isRouterAccessible: false, routerLocation: "", coverage: "", smartHome: { available: false, description: "" }, printer: { available: false, location: "", instructions: "" } },
  climateUtilities: { cooling: { available: false, types: [], otherType: "", instructions: {}, areas: [], acBedroomsCount: 0 }, heating: { available: false, types: [], otherType: "", instructions: {}, areas: [], acBedroomsCount: 0, radiator: { manual: false, instructions: "" }, underfloor: { manual: false, instructions: "" }, switchLocation: "" }, hotWater: { available: false, howProvided: "", otherHowProvided: "", manualOperation: false, activationMethod: "", panelLocation: "", switchLocation: "", labelForSwitch: false, labelContent: "", activationInstructions: "", heatingTime: "", turnOffInstructions: "" }, electricity: { keyCardRequired: false, keyCardLocation: "", keyCardSeparatelyDetails: "", panelAccessible: false, panelLocation: "", adaptersAvailable: false, adapterLocation: "" }, water: { drinkable: false, shutoffAccessible: false, shutoffLocation: "" } },
  livingSpaces: { bedrooms: "", rooms: [], livingRoom: { available: false, features: [], otherFeatures: "" }, outdoor: { available: false, types: [], otherTypes: "", features: [], otherFeatures: "" }, amenities: [], leisure: { features: [], otherFeatures: "" }, family: { allowed: false, babyCot: false, babyCotDetails: "", highChair: false, otherEquipment: "" } },
  kitchenDining: {
    type: "",
    appliances: [], otherAppliances: "",
    cookware: [], otherCookware: "",
    diningEssentials: [], otherDiningEssentials: "",
    coffeeSetup: [], otherCoffeeSetup: "",
    oven: { available: false, instructions: "" },
    dishwasher: { available: false, instructions: "" },
    complimentaryItems: [], otherComplimentaryItems: "",
    kitchenSupplies: [], otherKitchenSupplies: "",
    consumablesOccasionallyUnavailable: false
  },
  bathroomLaundry: {
    bathroomCount: 1,
    bathrooms: [{ id: "1", type: [], privacy: "" }],
    amenities: [], otherAmenities: "",
    plumbing: { useBinOnly: false },
    laundry: {
      washingMachine: { available: false, instructions: "" },
      dryer: { available: false, instructions: "" },
      dryingRack: false,
      iron: { available: false, location: "" },
      ironingBoard: { available: false, location: "" },
      nearbyServices: { available: false, services: [] }
    }
  },
  parkingTransport: {
    parking: {
      type: "",
      private: {
        type: "", spotAssignmentType: "", spotNumber: "", otherSpotSelection: "",
        accessRequired: false, accessMethod: "", evCharging: false, instructions: ""
      },
      street: { instructions: "" },
      paid: { name: "", address: "", hours: "", price: "", link: "" }
    },
    transfer: { available: false }
  },
  houseRules: { smoking: { policy: "", fee: { charged: false, amount: "", currency: "â‚¬" } }, visitors: { partiesAllowed: false, unregisteredAllowed: false }, pets: { allowed: false }, quietHours: { night: { from: "23:00", until: "08:00" }, afternoon: { apply: false, from: "15:00", until: "17:30" } }, cleaning: { midStayAvailable: false, midStayFee: { applicable: false, amount: "", currency: "â‚¬" }, extraLinenAvailable: false, extraLinenFee: { applicable: false, amount: "", currency: "â‚¬" } }, trashLocation: "" },
  localGuide: { essentialServices: [], restaurants: [], bars: [], attractions: [], hostTips: { personalRecommendation: "", bestTime: "" } },
  safetyMaintenance: {
    hospitals: [],
    emergency: {
      numbers: [
        { label: "European Emergency Number", number: "112", description: "For any urgent emergency situation, including police, ambulance, or fire services." },
        { label: "Police", number: "100", description: "For police assistance, crime reporting, safety concerns, or immediate law enforcement support." },
        { label: "Ambulance", number: "166", description: "For medical emergencies and urgent ambulance assistance." },
        { label: "Fire Service", number: "199", description: "For fires, smoke, rescue situations, or other fire-related emergencies." },
        { label: "Coast Guard", number: "108", description: "For sea-related emergencies, incidents near the coast, or urgent situations involving boats, ports, or swimmers." }
      ]
    },
    equipment: { fireExtinguisher: { available: false, location: "" }, smokeDetector: { available: false, location: "" }, carbonMonoxide: { available: false, location: "" }, firstAid: { available: false, location: "" }, cctv: { available: false, location: "" }, emergencyExit: { available: false, location: "" } }
  },
  policies: { bookingTerms: { definedByPlatform: true, customCancellationPolicy: "", refundsByTerms: true, customRefundPolicy: "", damageByPlatform: true, customDamagePolicy: "", localCompliance: true } },
  customKnowledge: { items: [] }
};

const STEP_TO_CATEGORIES = {
  1: ["property_identity", "property_location", "property_size"],
  2: ["building_recognition", "building_floor_access", "main_building_entrance", "main_entrance_intercom", "wheelchair_accessibility", "room_finding_instructions", "arrival_route"],
  3: ["main_guest_contact", "guest_contact_channels", "host_communication_languages"],
  4: ["check_in_methods_overview", "check_in_time_policy", "reception_office_check_in", "reception_office_required_check_in", "meet_on_arrival_check_in", "meet_on_arrival_late_arrival", "self_check_in_overview", "self_check_in_main_building_entrance", "self_check_in_main_building_entrance_hours", "self_check_in_building_entrance_device", "self_check_in_building_entrance_lockbox", "self_check_in_accommodation_door_device", "self_check_in_accommodation_lockbox", "self_check_in_access_code_timing", "early_check_in_policy", "guest_identification_requirements", "guest_identification_additional_notes", "guest_identification_tax_details", "self_check_in_access_code_document_requirement"],
  5: ["check_out_overview", "check_out_time_policy", "key_card_return_instructions", "check_out_tasks_and_cleaning", "late_check_out_policy", "stay_extension_policy", "main_building_exit_access"],
  6: ["luggage_storage_overview", "pre_check_in_luggage_storage", "post_check_out_luggage_storage", "nearby_luggage_lockers"],
  7: ["wifi_availability_and_credentials", "wifi_coverage_notes", "router_accessibility", "smart_home_devices", "printer_availability"],
  8: ["cooling_availability", "cooling_method_instructions", "heating_availability", "heating_method_instructions", "radiator_heating_manual_activation", "underfloor_heating_manual_activation", "hot_water_availability", "hot_water_manual_activation", "hot_water_manual_turn_off", "electricity_key_card_activation", "electrical_panel_accessibility", "plug_adapters_availability", "tap_water_drinkability", "water_shutoff_accessibility"],
  9: ["bedroom_sleeping_arrangements", "bedroom_features", "living_room_features", "outdoor_space_types", "outdoor_spaces", "outdoor_space_features", "leisure_facilities", "children_policy", "baby_and_child_equipment"],
  10: ["kitchen_availability_and_type", "kitchen_appliances", "oven_usage_instructions", "dishwasher_usage_instructions", "cookware_and_utensils", "dining_essentials", "coffee_setup", "complimentary_kitchen_items", "kitchen_supplies"],
  11: ["bathroom_layout_and_privacy", "bathroom_amenities", "bathroom_plumbing_rules", "washing_machine_availability", "clothes_dryer_availability", "drying_rack_availability", "iron_and_ironing_board_availability", "nearby_laundry_services"],
  12: ["parking_availability_and_type", "private_parking_access", "electric_vehicle_charging", "street_parking", "paid_nearby_parking", "transfer_service", "private_parking_availability"],
  13: ["smoking_policy", "smoking_violation_fee", "parties_and_events_policy", "unregistered_visitors_policy", "noise_policy", "quiet_hours_policy", "pet_policy", "pet_fee_policy"],
  14: ["local_guide_overview", "nearby_restaurant", "nearby_supermarket", "nearby_pharmacy", "nearby_attraction"],
  15: ["emergency_contact_numbers", "nearby_hospitals", "fire_extinguisher_availability", "smoke_detector_availability", "carbon_monoxide_detector_availability", "first_aid_kit_availability"],
  16: ["cancellation_and_modification_policy", "refund_policy", "damage_loss_and_excessive_cleaning_charges", "local_laws_registration_and_tax_compliance"],
  17: ["custom_knowledge"]
};

const KnowledgeBase2 = () => {
  const { theme } = useOutletContext();
  const isLight = theme === 'light';
  const { api, tabActiveHotelId } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialData);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // Fetch initial data (re-runs whenever the active tab property changes)
  useEffect(() => {
    // Guard: don't fetch if no property is selected or 'all' is active
    if (!tabActiveHotelId || tabActiveHotelId === 'all') return;

    const fetchKB = async () => {
      setIsLoading(true);
      try {
        // Pass hotelId as query param so the backend returns the correct property
        // even if the shared localStorage token belongs to a different property
        const res = await api.get(`/knowledge-base?hotelId=${tabActiveHotelId}`);
        const kbData = res.data?.data || res.data?.knowledgeData;

        if (kbData && typeof kbData === 'object' && Object.keys(kbData).length > 0) {
          const deepMerge = (target, source) => {
            const result = { ...target };
            for (const key in source) {
              if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
                result[key] = deepMerge(result[key] || {}, source[key]);
              } else {
                result[key] = source[key];
              }
            }
            return result;
          };
          setFormData(deepMerge(initialData, kbData));
        } else {
          setFormData(initialData);
        }
        setCurrentStep(1); // Reset to step 1 when property switches
      } catch (err) {
        console.error("Failed to fetch Knowledge Base", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchKB();
  }, [api, tabActiveHotelId]);

  // Synchronize completed steps with data validity
  useEffect(() => {
    if (isLoading) return;

    const completed = [];
    for (let i = 1; i <= 17; i++) {
      if (isStepValid(i)) {
        completed.push(i);
      } else {
        break; // Stop at first invalid step to maintain sequential progress state
      }
    }
    setCompletedSteps(completed);
  }, [formData, isLoading]);

  // Persistence: scope the draft cache key per property to prevent cross-tab bleed
  useEffect(() => {
    if (!tabActiveHotelId || tabActiveHotelId === 'all') return;
    localStorage.setItem(`kb2_data_${tabActiveHotelId}`, JSON.stringify(formData));
  }, [formData, tabActiveHotelId]);

  // Scroll to top on step change
  useEffect(() => {
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: 'instant' });
    } else {
      window.scrollTo(0, 0);
    }
    setShowValidation(false);
  }, [currentStep]);

  const updateFormData = (stepKey, dataOrFn) => {
    setFormData(prev => ({
      ...prev,
      [stepKey]: typeof dataOrFn === 'function' ? dataOrFn(prev[stepKey]) : dataOrFn
    }));
  };

  // Normalise derived fields and clear orphan data before sending to backend.
  // Ensures that data for disabled features or deselected methods is removed/reset.
  const normalizeFormData = (data) => {
    const cleanData = JSON.parse(JSON.stringify(data)); // Deep clone

    // --- Step 2: Building Access ---
    if (!cleanData.buildingAccess.hasMainEntrance) {
      cleanData.buildingAccess.intercom = initialData.buildingAccess.intercom;
    }

    // --- Step 4: Check-in ---
    const ci = cleanData.checkIn;
    if (!ci.checkInFrom) ci.checkInFrom = "15:00";
    if (!ci.checkInUntil) ci.checkInUntil = "22:00";
    if (ci.methods.includes("Reception/Office")) {
      if (!ci.reception.openFrom) ci.reception.openFrom = "08:00";
      if (!ci.reception.openUntil) ci.reception.openUntil = "20:00";
    } else {
      ci.reception = initialData.checkIn.reception;
    }
    if (ci.methods.includes("Meet on Arrival")) {
      if (!ci.meetOnArrival.from) ci.meetOnArrival.from = "08:00";
      if (!ci.meetOnArrival.until) ci.meetOnArrival.until = "20:00";
    } else {
      ci.meetOnArrival = initialData.checkIn.meetOnArrival;
    }
    if (!ci.methods.includes("Self Check-in")) {
      ci.selfCheckIn = initialData.checkIn.selfCheckIn;
    } else {
      // If Self Check-in is enabled, check sub-fields
      const sc = ci.selfCheckIn;
      if (!sc.hasDifferentDevices) {
        sc.buildingEntrance = undefined;
        sc.accommodation = undefined;
      }
      if (sc.mainEntranceAccessType !== "Yes, only during certain hours") {
        sc.mainEntranceAccessFrom = "";
        sc.mainEntranceAccessUntil = "";
      } else {
        if (!sc.mainEntranceAccessFrom) sc.mainEntranceAccessFrom = "08:00";
        if (!sc.mainEntranceAccessUntil) sc.mainEntranceAccessUntil = "20:00";
      }
    }
    if (!ci.earlyCheckIn.available) {
      ci.earlyCheckIn = initialData.checkIn.earlyCheckIn;
    }
    if (!ci.guestId.required) {
      ci.guestId = initialData.checkIn.guestId;
    } else if (!ci.methods.includes("Self Check-in")) {
      ci.guestId.requiredForAccessCode = false;
    }

    // --- Step 5: Check-out ---
    const co = cleanData.checkOut;
    if (!co.time) co.time = "11:00";
    if (co.buildingExit.accessType !== "No, the building exit opens freely from inside") {
      if (!co.buildingExit.accessFrom) co.buildingExit.accessFrom = "22:00";
      if (!co.buildingExit.accessUntil) co.buildingExit.accessUntil = "07:00";
    }
    if (!co.checkoutCleaning.expected) {
      co.checkoutCleaning = initialData.checkOut.checkoutCleaning;
    }
    if (!co.lateCheckOut.available) {
      co.lateCheckOut = initialData.checkOut.lateCheckOut;
    }
    if (!cleanData.buildingAccess.hasMainEntrance) {
      co.buildingExit = initialData.checkOut.buildingExit;
    }

    // --- Step 6: Luggage Storage ---
    const ls = cleanData.luggageStorage;
    if (!ls.available) {
      cleanData.luggageStorage = initialData.luggageStorage;
    } else {
      if (ls.beforeCheckIn.available) {
        if (!ls.beforeCheckIn.from) ls.beforeCheckIn.from = "08:00";
        if (!ls.beforeCheckIn.until) ls.beforeCheckIn.until = "15:00";
      } else {
        ls.beforeCheckIn = initialData.luggageStorage.beforeCheckIn;
      }
      if (ls.afterCheckOut.available) {
        if (!ls.afterCheckOut.from) ls.afterCheckOut.from = "11:00";
        if (!ls.afterCheckOut.until) ls.afterCheckOut.until = "18:00";
      } else {
        ls.afterCheckOut = initialData.luggageStorage.afterCheckOut;
      }
      if (!ls.nearbyLockers.available) {
        ls.nearbyLockers = initialData.luggageStorage.nearbyLockers;
      }
    }

    // --- Step 7: WiFi & Tech ---
    const wt = cleanData.wifiTech;
    if (!wt.available) {
      wt.ssid = "";
      wt.password = "";
      wt.isRouterAccessible = false;
      wt.routerLocation = "";
      wt.coverage = "";
    }
    if (!wt.isRouterAccessible) wt.routerLocation = "";
    if (!wt.smartHome.available) wt.smartHome = initialData.wifiTech.smartHome;
    if (!wt.printer.available) wt.printer = initialData.wifiTech.printer;

    // --- Step 8: Heating, Cooling & Utilities ---
    const cu = cleanData.climateUtilities;
    if (!cu.cooling.available) cu.cooling = initialData.climateUtilities.cooling;
    if (!cu.heating.available) cu.heating = initialData.climateUtilities.heating;
    if (!cu.hotWater.available) cu.hotWater = initialData.climateUtilities.hotWater;
    if (!cu.electricity.keyCardRequired) {
      cu.electricity.keyCardLocation = "";
      cu.electricity.keyCardSeparatelyDetails = "";
    }
    if (!cu.electricity.adaptersAvailable) cu.electricity.adapterLocation = "";
    if (!cu.water.shutoffAccessible) cu.water.shutoffLocation = "";

    // Derived logic for hot water panel (original normalizeFormData logic)
    const hw = cu.hotWater;
    const elec = cu.electricity;
    const isHotWaterPanelMethod = hw.manualOperation &&
      (hw.activationMethod === "Electrical panel switch" || hw.activationMethod === "Electrical panel button");

    if (isHotWaterPanelMethod) {
      elec.panelAccessible = true;
      elec.panelLocation = hw.panelLocation || elec.panelLocation || "";
    }

    // --- Step 9: Living Spaces ---
    const lsp = cleanData.livingSpaces;
    if (!lsp.livingRoom.available) lsp.livingRoom = initialData.livingSpaces.livingRoom;
    if (!lsp.outdoor.available) lsp.outdoor = initialData.livingSpaces.outdoor;
    if (!lsp.family.allowed) lsp.family = initialData.livingSpaces.family;

    // --- Step 11: Bathroom & Laundry ---
    const bl = cleanData.bathroomLaundry;
    if (!bl.laundry.washingMachine.available) bl.laundry.washingMachine = initialData.bathroomLaundry.laundry.washingMachine;
    if (!bl.laundry.dryer.available) bl.laundry.dryer = initialData.bathroomLaundry.laundry.dryer;
    if (!bl.laundry.iron.available) bl.laundry.iron.location = "";
    if (!bl.laundry.ironingBoard.available) bl.laundry.ironingBoard.location = "";
    if (!bl.laundry.nearbyServices.available) bl.laundry.nearbyServices = initialData.bathroomLaundry.laundry.nearbyServices;

    // --- Step 10: Kitchen & Dining ---
    const kd = cleanData.kitchenDining;
    if (kd.type === "No Kitchen") {
      kd.appliances = [];
      kd.otherAppliances = "";
      kd.cookware = [];
      kd.otherCookware = "";
      kd.diningEssentials = [];
      kd.otherDiningEssentials = "";
      kd.coffeeSetup = [];
      kd.otherCoffeeSetup = "";
      kd.oven = initialData.kitchenDining.oven;
      kd.dishwasher = initialData.kitchenDining.dishwasher;
      kd.complimentaryItems = [];
      kd.otherComplimentaryItems = "";
      kd.kitchenSupplies = [];
      kd.otherKitchenSupplies = "";
    } else {
      if (!kd.oven.available) kd.oven = initialData.kitchenDining.oven;
      if (!kd.dishwasher.available) kd.dishwasher = initialData.kitchenDining.dishwasher;
    }

    // --- Step 12: Parking & Transport ---
    const pt = cleanData.parkingTransport;
    if (pt.parking.type !== "Private On-site") pt.parking.private = initialData.parkingTransport.parking.private;
    if (pt.parking.type !== "Street Parking") pt.parking.street = initialData.parkingTransport.parking.street;
    if (pt.parking.type !== "Paid Nearby") pt.parking.paid = initialData.parkingTransport.parking.paid;

    // --- Step 13: House Rules ---
    const hr = cleanData.houseRules;
    if (!hr.quietHours.night.from) hr.quietHours.night.from = "23:00";
    if (!hr.quietHours.night.until) hr.quietHours.night.until = "08:00";
    if (hr.quietHours.afternoon.apply) {
      if (!hr.quietHours.afternoon.from) hr.quietHours.afternoon.from = "15:00";
      if (!hr.quietHours.afternoon.until) hr.quietHours.afternoon.until = "17:30";
    } else {
      hr.quietHours.afternoon = initialData.houseRules.quietHours.afternoon;
    }
    if (!hr.smoking.fee.charged) {
      hr.smoking.fee.amount = "";
    }
    if (!hr.cleaning.midStayAvailable) {
      hr.cleaning.midStayFee = initialData.houseRules.cleaning.midStayFee;
    }
    if (!hr.cleaning.extraLinenAvailable) {
      hr.cleaning.extraLinenFee = initialData.houseRules.cleaning.extraLinenFee;
    }

    // --- Step 15: Safety & Emergency ---
    const sm = cleanData.safetyMaintenance;
    Object.keys(sm.equipment).forEach(key => {
      if (!sm.equipment[key].available) {
        sm.equipment[key] = initialData.safetyMaintenance.equipment[key];
      }
    });

    return cleanData;
  };

  const handleNext = () => {
    if (!isStepValid(currentStep)) {
      setShowValidation(true);
      return;
    }

    if (currentStep < STEPS.length) {
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps(prev => [...prev, currentStep]);
      }
      setCurrentStep(prev => prev + 1);
    } else {
      // Final step: Save
      handleSave();
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const res = await api.post('/run-template-chunker', { knowledgeData: normalizeFormData(formData) });
      if (res.data?.success) {
        // We could show a success message or redirect
        // For now, let's just mark it as saved (the child component Step17 will show the success view if we pass down the state)
        setCompletedSteps(prev => [...prev, 18]);
      } else {
        alert(res.data?.error || "Failed to save Knowledge Base");
      }
    } catch (err) {
      console.error("Save error", err);
      alert("An error occurred while saving.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const goToStep = (id) => {
    // Always allow going back
    if (id < currentStep) {
      setCurrentStep(id);
      return;
    }

    // Allow going to any step if all steps before it are valid
    const isReachable = id === 1 || STEPS.slice(0, id - 1).every(s => isStepValid(s.id));
    if (isReachable) {
      setCurrentStep(id);
    }
  };

  const activeStep = STEPS.find(s => s.id === currentStep);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const isStepTouched = (stepId) => {
    const step = STEPS.find(s => s.id === stepId);
    if (!step || !step.key) return false;
    if (stepId === 18) return currentStep === 18;
    return JSON.stringify(formData[step.key]) !== JSON.stringify(initialData[step.key]);
  };


  const isStepValid = (stepId) => {
    switch (stepId) {
      case 1: // Property Profile
        const pp = formData.propertyProfile;
        return !!pp.name && !!pp.type && !!pp.address && !!pp.floor && !!pp.size;
      case 2: // Building Access
        const ba = formData.buildingAccess;
        if (!ba.buildingAccessType || !ba.roomFindingInstructions) return false;
        if (ba.intercom.available && !ba.intercom.instructions) return false;
        return true;
      case 3: // Contact Details
        const cd = formData.contactDetails;
        return !!cd.mainContactType && !!cd.name && !!cd.phone && !!cd.email && emailRegex.test(cd.email) && !!cd.availabilityHours && cd.languages.length > 0;
      case 4: { // Check-in
        const checkIn = formData.checkIn;
        if (checkIn.methods.length === 0) return false;
        if (checkIn.methods.includes("Reception/Office")) {
          if (!checkIn.reception?.location) return false;
        }
        if (checkIn.methods.includes("Meet on Arrival")) {
          if (!checkIn.meetOnArrival?.meetingPoint || !checkIn.meetOnArrival?.instructions || !checkIn.meetOnArrival?.afterWindowInstructions) return false;
        }
        if (checkIn.methods.includes("Self Check-in")) {
          const sc = checkIn.selfCheckIn;
          if (sc?.hasDifferentDevices) {
            // Two separate devices: validate both sub-objects
            const be = sc.buildingEntrance || {};
            const acc = sc.accommodation || {};
            if (!be.deviceType || !be.location || !be.accessCodeTiming) return false;
            if (!be.accessCodeChannels || be.accessCodeChannels.length === 0) return false;
            if (be.accessCodeChannels.includes("Other") && !be.accessCodeChannelOther) return false;
            if (!acc.deviceType || !acc.location || !acc.accessCodeTiming) return false;
            if (!acc.accessCodeChannels || acc.accessCodeChannels.length === 0) return false;
            if (acc.accessCodeChannels.includes("Other") && !acc.accessCodeChannelOther) return false;
          } else {
            // Single device: validate root selfCheckIn fields
            if (!sc?.deviceType || !sc?.location || !sc?.accessCodeTiming) return false;
            if (!sc?.accessCodeChannels || sc.accessCodeChannels.length === 0) return false;
            if (sc.accessCodeChannels.includes("Other") && !sc.accessCodeChannelOther) return false;
          }
        }
        if (!checkIn.checkInType) return false;
        if (checkIn.earlyCheckIn.available && checkIn.earlyCheckIn.feeApplicable && !checkIn.earlyCheckIn.feeAmount) return false;
        if (checkIn.guestId.required) {
          if (checkIn.guestId.channels.length === 0 || !checkIn.guestId.requiredDocuments) return false;
          if (checkIn.guestId.channels.includes("Email Address") && (!checkIn.guestId.email || !emailRegex.test(checkIn.guestId.email))) return false;
          if (checkIn.guestId.channels.includes("Messaging Apps") && (!checkIn.guestId.phone || !checkIn.guestId.messagingApp)) return false;
        }
        return true;
      }
      case 5: { // Check-out
        const co = formData.checkOut;
        if (!co.keyReturn) return false;
        if (co.lateCheckOut.available && co.lateCheckOut.feeApplicable && !co.lateCheckOut.feeAmount) return false;
        if (co.checkoutCleaning.expected && co.checkoutCleaning.tasks.length === 0) return false;
        if (co.buildingExit?.accessType?.includes("Yes") && !co.buildingExit.exitInstructions) return false;
        return true;
      }
      case 6: { // Luggage Storage
        const ls = formData.luggageStorage;
        if (ls.available) {
          if (!ls.beforeCheckIn.available && !ls.afterCheckOut.available) return false;
          if (ls.beforeCheckIn.available && (!ls.beforeCheckIn.location || !ls.beforeCheckIn.instructions)) return false;
          if (ls.afterCheckOut.available && (!ls.afterCheckOut.location || !ls.afterCheckOut.instructions)) return false;
        }
        if (ls.nearbyLockers.available) {
          if (ls.nearbyLockers.lockers.length === 0) return false;
          for (const locker of ls.nearbyLockers.lockers) {
            if (!locker.name || !locker.address) return false;
          }
        }
        return true;
      }
      case 7: { // WiFi & Tech
        const wt = formData.wifiTech;
        if (wt.available) {
          if (!wt.ssid || !wt.password) return false;
        }
        if (wt.isRouterAccessible && !wt.routerLocation) return false;
        if (wt.smartHome.available && !wt.smartHome.description) return false;
        if (wt.printer.available && !wt.printer.location) return false;
        return true;
      }
      case 8: { // Heating, Cooling & Utilities
        const cu = formData.climateUtilities;
        if (cu.cooling.available && cu.cooling.types.length === 0) return false;
        if (cu.heating.available && cu.heating.types.length === 0) return false;
        if (cu.hotWater.available) {
          if (!cu.hotWater.howProvided) return false;
          if (cu.hotWater.howProvided === "Other" && !cu.hotWater.otherHowProvided) return false;
          if (!cu.hotWater.heatingTime) return false;
          if (cu.hotWater.manualOperation) {
            if (!cu.hotWater.activationMethod || !cu.hotWater.activationInstructions) return false;
            if ((cu.hotWater.activationMethod === "Electrical panel switch" || cu.hotWater.activationMethod === "Electrical panel button") && !cu.hotWater.panelLocation) return false;
            if (cu.hotWater.activationMethod === "Wall switch" && !cu.hotWater.switchLocation) return false;
            if (cu.hotWater.labelForSwitch && !cu.hotWater.labelContent) return false;
          }
          if (!cu.hotWater.turnOffInstructions) return false;
        }
        if (cu.electricity.keyCardRequired) {
          if (!cu.electricity.keyCardLocation) return false;
          if (cu.electricity.keyCardLocation === "Provided separately" && !cu.electricity.keyCardSeparatelyDetails) return false;
        }
        // Derive panelAccessible: if hot water uses a panel method, it's implicitly accessible
        const panelMethodActive = cu.hotWater.manualOperation &&
          (cu.hotWater.activationMethod === "Electrical panel switch" || cu.hotWater.activationMethod === "Electrical panel button");
        const effectivePanelAccessible = panelMethodActive ? true : cu.electricity.panelAccessible;
        // Check panel location from the right source
        if (effectivePanelAccessible && !cu.electricity.panelLocation && !cu.hotWater.panelLocation) return false;
        if (cu.electricity.adaptersAvailable && !cu.electricity.adapterLocation) return false;
        if (cu.water.shutoffAccessible && !cu.water.shutoffLocation) return false;
        return true;
      }
      case 9: { // Living Spaces
        const ls = formData.livingSpaces;
        if (!ls.bedrooms || ls.bedrooms === 0) return false;
        for (const room of ls.rooms) {
          const totalBeds = Object.values(room.beds).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0);
          if (totalBeds === 0) return false;
          if (room.features.includes("Other") && !room.otherFeatures) return false;
        }
        if (ls.livingRoom.available) {
          if (ls.livingRoom.features.includes("Other") && !ls.livingRoom.otherFeatures) return false;
        }
        if (ls.outdoor.available) {
          if (ls.outdoor.types.length === 0) return false;
          if (ls.outdoor.types.includes("Other") && !ls.outdoor.otherTypes) return false;
          if (ls.outdoor.features.includes("Other") && !ls.outdoor.otherFeatures) return false;
        }
        if (ls.leisure.features.includes("Other") && !ls.leisure.otherFeatures) return false;
        if (ls.family.allowed && ls.family.babyCot && !ls.family.babyCotDetails) return false;
        return true;
      }
      case 10: { // Kitchen & Dining
        const kd = formData.kitchenDining;
        if (!kd.type) return false;

        if (kd.appliances.includes("Other") && !kd.otherAppliances) return false;
        if (kd.appliances.includes("Oven") && !kd.oven.instructions) return false;
        if (kd.appliances.includes("Dishwasher") && !kd.dishwasher.instructions) return false;

        if (kd.cookware.includes("Other") && !kd.otherCookware) return false;
        if (kd.diningEssentials.includes("Other") && !kd.otherDiningEssentials) return false;
        if (kd.coffeeSetup.includes("Other") && !kd.otherCoffeeSetup) return false;
        if (kd.complimentaryItems.includes("Other") && !kd.otherComplimentaryItems) return false;
        if (kd.kitchenSupplies.includes("Other") && !kd.otherKitchenSupplies) return false;

        return true;
      }
      case 11: { // Bathroom & Laundry
        const bl = formData.bathroomLaundry;
        if (!bl.bathroomCount || bl.bathroomCount === 0) return false;
        for (const b of bl.bathrooms) {
          if (b.type.length === 0 || !b.privacy) return false;
        }
        if (bl.amenities.includes("Other") && !bl.otherAmenities) return false;
        if (bl.laundry.iron.available && !bl.laundry.iron.location) return false;
        if (bl.laundry.ironingBoard.available && !bl.laundry.ironingBoard.location) return false;
        const ns = bl.laundry.nearbyServices;
        if (ns?.available) {
          if (!ns.services || ns.services.length === 0) return false;
          for (const s of ns.services) {
            if (!s.name || !s.address) return false;
          }
        }
        return true;
      }
      case 12: { // Parking & Transport
        const pt = formData.parkingTransport;
        if (!pt.parking.type) return false;
        if (pt.parking.type === "Private On-site") {
          const p = pt.parking.private;
          if (!p.type || !p.spotAssignmentType) return false;
          if (p.spotAssignmentType === "Parking spot number" && !p.spotNumber) return false;
          if (p.spotAssignmentType === "Other" && !p.otherSpotSelection) return false;
          if (p.accessRequired && !p.accessMethod) return false;
        }
        if (pt.parking.type === "Street Parking" && !pt.parking.street?.instructions) return false;
        return true;
      }
      case 13: { // House Rules
        const hr = formData.houseRules;
        if (!hr.smoking.policy) return false;
        if (hr.smoking.fee.charged && !hr.smoking.fee.amount) return false;
        if (hr.cleaning.midStayAvailable && hr.cleaning.midStayFee?.applicable && !hr.cleaning.midStayFee?.amount) return false;
        if (hr.cleaning.extraLinenAvailable && hr.cleaning.extraLinenFee?.applicable && !hr.cleaning.extraLinenFee?.amount) return false;
        if (!hr.trashLocation) return false;
        return true;
      }
      case 14: { // Local Guide
        const lg = formData.localGuide;
        for (const s of lg.essentialServices) if (!s.type || !s.name) return false;
        for (const r of lg.restaurants) if (!r.category || !r.name) return false;
        for (const b of lg.bars) if (!b.type || !b.name) return false;
        for (const a of lg.attractions) if (!a.type || !a.name) return false;
        return true;
      }
      case 15: { // Safety & Emergency
        const sm = formData.safetyMaintenance;
        // Validate numbers ONLY if they exist
        for (const num of sm.emergency.numbers) {
          if (!num.label || !num.number) return false;
        }
        for (const key in sm.equipment) {
          const eq = sm.equipment[key];
          if (eq.available && !eq.location && key !== 'smokeDetector' && key !== 'carbonMonoxide') return false;
        }
        return true;
      }
      case 16: { // Policies
        const p = formData.policies.bookingTerms;
        // Require at least one choice to be explicitly confirmed/present
        if (!p.definedByPlatform && !p.customCancellationPolicy) return false;
        if (!p.refundsByTerms && !p.customRefundPolicy) return false;
        if (!p.damageByPlatform && !p.customDamagePolicy) return false;
        return true;
      }
      case 17: { // Custom Knowledge
        const customItems = formData.customKnowledge?.items || [];
        for (const item of customItems) {
          const hasAnyValue = [
            item.title,
            item.category,
            item.information,
          ].some((value) => String(value || "").trim());

          if (hasAnyValue && (!item.title || !item.category || !item.information)) {
            return false;
          }
        }
        return true;
      }
      default:
        return true;
    }
  };

  const renderStep = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-40 gap-4">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
          <p className={isLight ? 'text-neutral-500' : 'text-neutral-400'}>Loading Knowledge Base...</p>
        </div>
      );
    }

    const stepContent = (() => {
      switch (currentStep) {
        case 1:
          return <Step1 data={formData.propertyProfile} updateData={updateFormData} isLight={isLight} showValidation={showValidation} />;
        case 2:
          return <Step2 data={formData.buildingAccess} updateData={updateFormData} isLight={isLight} showValidation={showValidation} />;
        case 3:
          return <Step3 data={formData.contactDetails} updateData={updateFormData} isLight={isLight} showValidation={showValidation} />;
        case 4:
          return <Step4 data={formData.checkIn} updateData={updateFormData} isLight={isLight} showValidation={showValidation} buildingAccess={formData.buildingAccess} />;
        case 5:
          return <Step5 data={formData.checkOut} updateData={updateFormData} isLight={isLight} showValidation={showValidation} buildingAccess={formData.buildingAccess} />;
        case 6:
          return <Step6 data={formData.luggageStorage} updateData={updateFormData} isLight={isLight} showValidation={showValidation} propertyLocation={{ lat: formData.propertyProfile.lat, lon: formData.propertyProfile.lon }} />;
        case 7:
          return <Step7 data={formData.wifiTech} updateData={updateFormData} isLight={isLight} showValidation={showValidation} />;
        case 8:
          return <Step8 data={formData.climateUtilities} updateData={updateFormData} isLight={isLight} showValidation={showValidation} />;
        case 9:
          const isACAvailable = formData.climateUtilities.cooling.types.includes("Air conditioning") || formData.climateUtilities.heating.types.includes("Air conditioning");
          const isCeilingFanAvailable = formData.climateUtilities.cooling.types.includes("Ceiling fan");
          return (
            <Step9
              data={formData.livingSpaces}
              updateData={updateFormData}
              isLight={isLight}
              showValidation={showValidation}
              isACAvailable={isACAvailable}
              isCeilingFanAvailable={isCeilingFanAvailable}
            />
          );
        case 10:
          return <Step10 data={formData.kitchenDining} updateData={updateFormData} isLight={isLight} showValidation={showValidation} />;
        case 11:
          return <Step11 data={formData.bathroomLaundry} updateData={updateFormData} isLight={isLight} showValidation={showValidation} propertyLocation={{ lat: formData.propertyProfile.lat, lon: formData.propertyProfile.lon }} />;
        case 12:
          return <Step12 data={formData.parkingTransport} updateData={updateFormData} isLight={isLight} showValidation={showValidation} />;
        case 13:
          return <Step13 data={formData.houseRules} updateData={updateFormData} isLight={isLight} showValidation={showValidation} />;
        case 14:
          return <Step14 data={formData.localGuide} updateData={updateFormData} isLight={isLight} showValidation={showValidation} propertyLocation={{ lat: formData.propertyProfile.lat, lon: formData.propertyProfile.lon }} />;
        case 15:
          return <Step15 data={formData.safetyMaintenance} updateData={updateFormData} isLight={isLight} showValidation={showValidation} propertyLocation={{ lat: formData.propertyProfile.lat, lon: formData.propertyProfile.lon }} />;
        case 16:
          return <Step16 data={formData.policies} updateData={updateFormData} isLight={isLight} showValidation={showValidation} />;
        case 17:
          return <Step17CustomKnowledge data={formData.customKnowledge} updateData={updateFormData} isLight={isLight} />;
        case 18:
          return (
            <Step17
              data={formData}
              goToStep={goToStep}
              isLight={isLight}
              api={api}
              isStepValid={isStepValid}
              isStepTouched={isStepTouched}
              isSaving={isSaving}
              handleSave={handleSave}
              completedSteps={completedSteps}
            />
          );
        default:
          return (
            <div className={`p-20 text-center ${isLight ? 'text-neutral-400' : 'text-neutral-600'}`}>
              Step {currentStep} content ({activeStep.name}) will be loaded here.
            </div>
          );
      }
    })();

    return (
      <FormContext.Provider value={{ showValidation }}>
        {stepContent}
      </FormContext.Provider>
    );
  };

  return (
    <div className="relative">
      {/* Liquid background blobs (dark mode only) */}
      {!isLight && (
        <div className="pointer-events-none fixed inset-0 -z-10">
          <div className="absolute -top-32 -left-24 w-96 h-96 rounded-full bg-gradient-to-br from-white/6 to-white/0 blur-3xl opacity-70" />
          <div className="absolute -bottom-40 -right-10 w-[420px] h-[420px] rounded-full bg-gradient-to-tr from-white/4 to-white/0 blur-3xl opacity-60" />
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-gradient-to-b from-white/5 to-transparent blur-3xl opacity-40" />
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6 min-h-[calc(100vh-160px)]">
        {/* Sidebar Navigation */}
        <aside
          className={`lg:w-80 flex-shrink-0 border rounded-3xl backdrop-blur-2xl sticky top-28 h-[calc(100vh-140px)] flex flex-col overflow-hidden transition-all duration-300 ${isLight
            ? "bg-white border-neutral-200 shadow-[0_18px_40px_rgba(15,23,42,0.12)]"
            : "bg-white/5 border-white/5 shadow-[0_24px_70px_rgba(0,0,0,0.75)]"
            }`}
        >
          <div className={`p-6 border-b ${isLight ? 'border-neutral-100' : 'border-white/5'}`}>
            <div className={`text-[10px] uppercase tracking-[0.18em] mb-0.5 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>Setup Progress</div>
            <h3 className="text-base font-semibold tracking-tight">Onboarding Wizard</h3>
            <p className={`text-[10px] mt-1 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>
              {STEPS.filter(s => s.id < 18 && completedSteps.includes(s.id)).length} of 17 steps completed
            </p>
            <div className={`mt-4 h-1.5 w-full rounded-full overflow-hidden ${isLight ? 'bg-neutral-100' : 'bg-white/5'}`}>
              <motion.div
                className="h-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]"
                initial={{ width: 0 }}
                animate={{ width: `${(STEPS.filter(s => s.id < 18 && completedSteps.includes(s.id)).length / 17) * 100}%` }}
              />
            </div>
          </div>

          <div className={`flex-1 overflow-y-auto ${isLight ? 'custom-scroll-light' : 'custom-scroll'}`}>
            {STEPS.map((step) => {
              const isCompleted = isStepValid(step.id) && step.id < 18;
              const isActive = currentStep === step.id;
              const isLocked = step.id > 1 && !STEPS.slice(0, step.id - 1).every(s => isStepValid(s.id));

              return (
                <button
                  key={step.id}
                  onClick={() => !isLocked && goToStep(step.id)}
                  disabled={isLocked}
                  className={`w-full flex items-center gap-3 px-6 py-4 text-left transition-all relative group ${isActive
                    ? isLight
                      ? "bg-neutral-900 text-white shadow-[0_12px_30px_rgba(15,23,42,0.55)]"
                      : "bg-white/15 text-neutral-50 shadow-[0_12px_30px_rgba(0,0,0,0.8)]"
                    : isLocked
                      ? "opacity-30 cursor-not-allowed"
                      : isLight
                        ? "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
                        : "text-neutral-400 hover:bg-white/5 hover:text-neutral-100"
                    }`}
                >
                  <div className="relative">
                    <div className={`h-8 w-8 rounded-xl flex items-center justify-center border transition-all ${isActive
                      ? "border-white/20 bg-white/10"
                      : isLight ? "bg-neutral-50 border-neutral-200 text-neutral-400" : "bg-white/5 border-white/10 text-neutral-500"
                      }`}>
                      <step.icon size={14} />
                    </div>
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium truncate">{step.name}</span>
                    <span className={`text-[9px] uppercase tracking-wider font-bold ${isActive ? 'text-white/60' : 'text-neutral-500'}`}>
                      Step {step.id}
                    </span>
                  </div>
                  {isActive && (
                    <div className="ml-auto h-1.5 w-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
                  )}
                </button>
              );
            })}
          </div>
        </aside>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col gap-6">
          <div className={`rounded-3xl backdrop-blur-2xl border p-6 sm:p-10 min-h-[650px] flex flex-col transition-all duration-300 ${isLight
            ? 'bg-white border-neutral-200 shadow-[0_18px_40px_rgba(15,23,42,0.12)]'
            : 'bg-white/5 border-white/5 shadow-[0_24px_70px_rgba(0,0,0,0.75)]'
            }`}>
            <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-10 pb-8 border-b border-white/5">
              <div className="flex items-center gap-5">
                <div className={`h-12 w-12 rounded-2xl flex items-center justify-center border shadow-inner transition-all ${isLight ? 'bg-neutral-50 border-neutral-200 text-neutral-600' : 'bg-white/5 border-white/15 text-neutral-300'
                  }`}>
                  <activeStep.icon size={24} />
                </div>
                <div>
                  <div className={`text-[10px] uppercase tracking-[0.18em] mb-0.5 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                    {currentStep === 18 ? 'Final Step' : `Section ${currentStep} of 17`}
                  </div>
                  <h2 className="text-2xl font-semibold tracking-tight">{activeStep.name}</h2>
                </div>
              </div>
              <div className={`hidden sm:flex items-center px-4 py-2 rounded-full border text-[10px] font-bold uppercase tracking-widest ${isLight ? 'bg-neutral-50 border-neutral-200 text-neutral-500' : 'bg-white/5 border-white/10 text-neutral-500'
                }`}>
                {Math.round((STEPS.filter(s => s.id < 18 && completedSteps.includes(s.id)).length / 17) * 100)}% Complete
              </div>
            </header>

            <div className="flex-1">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  {renderStep()}
                </motion.div>
              </AnimatePresence>
            </div>

            <footer className={`mt-10 pt-8 border-t flex items-center justify-between ${isLight ? 'border-neutral-100' : 'border-white/5'}`}>
              <button
                onClick={handleBack}
                disabled={currentStep === 1}
                className={`px-6 py-3 rounded-2xl font-medium text-sm transition-all flex items-center gap-2 ${currentStep === 1
                  ? 'opacity-0 pointer-events-none'
                  : isLight ? 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200' : 'bg-white/5 text-neutral-300 hover:bg-white/10'
                  }`}
              >
                <ChevronLeft size={18} />
                <span>Back</span>
              </button>

              <div className="flex items-center gap-3">

                <button
                  onClick={handleNext}
                  disabled={isSaving}
                  className={`px-8 py-3 rounded-2xl font-bold text-sm tracking-tight transition-all active:scale-95 flex items-center gap-2 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''
                    } ${isLight
                      ? 'bg-neutral-900 text-white hover:bg-neutral-800 shadow-[0_12px_30px_rgba(15,23,42,0.3)]'
                      : 'bg-white text-black hover:bg-neutral-200 shadow-[0_20px_40px_-10px_rgba(255,255,255,0.2)]'
                    }`}
                >
                  {isSaving ? <Loader2 size={18} className="animate-spin" /> : null}
                  <span>{currentStep === STEPS.length ? 'Save Knowledge Base' : 'Continue'}</span>
                  <ChevronRight size={18} />
                </button>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBase2;


